import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches React errors and provides user-friendly feedback.
 * Specifically handles MSAL interaction_in_progress errors gracefully.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🛑 Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });

    // Check for MSAL interaction error
    const errorMessage = error?.message || '';
    const isInteractionError =
      errorMessage.includes('interaction_in_progress') ||
      errorMessage.includes('interaction is currently in progress');

    if (isInteractionError) {
      console.log(
        '⏳ MSAL interaction in progress detected, auto-recovering...'
      );
      // Auto-recover after 2 seconds
      setTimeout(() => {
        this.setState({ hasError: false, error: null, errorInfo: null });
      }, 2000);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const errorMessage = this.state.error?.message || 'Unknown error';
    const isInteractionError =
      errorMessage.includes('interaction_in_progress') ||
      errorMessage.includes('interaction is currently in progress');

    // Show waiting message for interaction errors
    if (isInteractionError) {
      return (
        <div style={styles.overlayStyle}>
          <div style={styles.modalStyle}>
            <h2 style={styles.titleStyle}>⏳ Authenticating</h2>
            <p style={styles.messageStyle}>
              Please wait while we complete the authentication process...
            </p>
            <div style={styles.spinnerStyle}></div>
          </div>
        </div>
      );
    }

    // Show error UI for other errors
    return (
      <div style={styles.overlayStyle}>
        <div style={styles.modalStyle}>
          <h2 style={styles.errorTitleStyle}>🛑 Something went wrong</h2>
          <p style={styles.messageStyle}>
            An unexpected error occurred. You can try to continue or reload the
            page.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <details style={styles.detailsStyle}>
              <summary style={styles.summaryStyle}>Error Details</summary>
              <pre style={styles.preStyle}>
                {errorMessage}
                {this.state.errorInfo && (
                  <>
                    {'\n\n'}
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </pre>
            </details>
          )}
          <div style={styles.buttonContainerStyle}>
            <button onClick={this.handleReset} style={styles.buttonStyle}>
              Continue
            </button>
            <button
              onClick={this.handleReload}
              style={{ ...styles.buttonStyle, ...styles.secondaryButtonStyle }}>
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  overlayStyle: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modalStyle: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    textAlign: 'center' as const,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  titleStyle: {
    margin: '0 0 1rem 0',
    color: '#333',
    fontSize: '1.5rem',
  },
  errorTitleStyle: {
    margin: '0 0 1rem 0',
    color: '#d32f2f',
    fontSize: '1.5rem',
  },
  messageStyle: {
    margin: '0 0 1.5rem 0',
    color: '#666',
    fontSize: '1rem',
    lineHeight: '1.5',
  },
  spinnerStyle: {
    margin: '1rem auto',
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #0078d4',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  buttonContainerStyle: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
  },
  buttonStyle: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#0078d4',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500 as const,
    transition: 'background-color 0.2s',
  },
  secondaryButtonStyle: {
    backgroundColor: '#6c757d',
  },
  detailsStyle: {
    marginTop: '1rem',
    textAlign: 'left' as const,
    fontSize: '0.875rem',
  },
  summaryStyle: {
    cursor: 'pointer',
    fontWeight: 600 as const,
    marginBottom: '0.5rem',
  },
  preStyle: {
    backgroundColor: '#f5f5f5',
    padding: '1rem',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: '200px',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap' as const,
    wordWrap: 'break-word' as const,
  },
};

// Add keyframe animation for spinner
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}
